import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { 
  Globe, 
  Smartphone, 
  HeadphonesIcon, 
  ArrowRight, 
  Mail, 
  Menu, 
  X,
  CheckCircle,
  Download,
  Play
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header className={`header-nav ${isScrolled ? "scrolled" : ""}`}>
      <div className="section-container py-4">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="text-2xl font-bold text-[#0A192F]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Lotus<span className="text-[#004EEB]">Infotek</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-[#0A192F] hover:text-[#004EEB] transition-colors"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact">
              <Button 
                className="btn-primary rounded-none"
                data-testid="nav-get-started"
              >
                Get Started
              </Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden pt-4 pb-2 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-[#0A192F] hover:text-[#004EEB] transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="btn-primary rounded-none w-full">
                Get Started
              </Button>
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

// Speak Daily Banner Component
const SpeakDailyBanner = () => {
  return (
    <div className="bg-gradient-to-r from-[#004EEB] to-[#003BBB] py-5" data-testid="speak-daily-banner">
      <div className="section-container">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Smartphone size={28} className="text-white" />
            </div>
            <div className="text-white">
              <p className="font-bold text-xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Speak Daily — Talk English in 28 Days
              </p>
              <p className="text-white/80 text-sm">
                AI-powered English learning app with daily lessons, speech recognition & progress tracking
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a 
              href="https://play.google.com/store/apps/details?id=com.lotusinfotek.englishin28days" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#0A192F] px-5 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors"
              data-testid="play-store-link"
            >
              <Play size={16} /> Play Store
            </a>
            <a 
              href="https://apps.apple.com/app/english-in-28-days/id123456789" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#0A192F] px-5 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors"
              data-testid="app-store-link"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              App Store
            </a>
            <a 
              href="https://lotusinfotek.com/downloads/english-in-28-days.apk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/20 text-white border border-white/30 px-5 py-2.5 text-sm font-medium hover:bg-white/30 transition-colors"
              data-testid="direct-download-link"
            >
              <Download size={16} /> Direct Download
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section">
      {/* Spacer for fixed header */}
      <div className="h-[72px]"></div>
      
      {/* Speak Daily Banner */}
      <SpeakDailyBanner />
      
      <div className="section-container py-16 md:py-24 bg-gradient-to-b from-[#F4F6F8] to-white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
          {/* Left Content - 7 columns */}
          <div className="md:col-span-7 space-y-8">
            <div className="space-y-6 opacity-0 animate-fade-in-up">
              <p className="overline">Lotus Infotek</p>
              <h1 
                className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A192F] leading-[1.1]"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                People over profit.
                <br />
                <span className="text-[#004EEB]">Service over sales.</span>
              </h1>
              <p className="text-lg text-[#475569] max-w-xl leading-relaxed">
                Cost effective, reliable and robust software solutions for individuals 
                and private companies. Your success is our priority.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up animation-delay-200">
              <a href="#contact">
                <Button 
                  className="btn-primary rounded-none inline-flex items-center gap-2"
                  data-testid="hero-cta-primary"
                >
                  Start a Project <ArrowRight size={18} />
                </Button>
              </a>
              <a href="#services">
                <Button 
                  className="btn-secondary rounded-none"
                  data-testid="hero-cta-secondary"
                >
                  Our Services
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - 5 columns */}
          <div className="md:col-span-5 opacity-0 animate-fade-in-up animation-delay-300">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1756481602921-863b17ce6f83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJsdWUlMjBnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1NzE0MDk0fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern abstract architecture"
                className="w-full h-[50vh] md:h-[70vh] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/10 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Globe size={28} />,
      title: "Web Development",
      description: "Modern, responsive web applications built with cutting-edge technologies. From landing pages to complex enterprise solutions.",
      features: ["Custom Web Apps", "E-commerce", "CMS Solutions"]
    },
    {
      icon: <HeadphonesIcon size={28} />,
      title: "IT Support",
      description: "Reliable technical support and maintenance services. We keep your systems running smoothly 24/7.",
      features: ["24/7 Support", "System Maintenance", "Security Updates"]
    },
    {
      icon: <Smartphone size={28} />,
      title: "Mobile Apps",
      description: "User-centric mobile applications for iOS and Android. Engaging experiences that your users will love.",
      features: ["iOS & Android", "Cross-Platform", "UI/UX Design"]
    }
  ];

  return (
    <section id="services" className="section-padding bg-white" data-testid="services-section">
      <div className="section-container">
        <div className="max-w-2xl mb-16">
          <p className="overline mb-4">What We Do</p>
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A192F] mb-6"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Services built for your success
          </h2>
          <p className="text-lg text-[#475569]">
            We deliver comprehensive software solutions tailored to your unique needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={service.title} 
              className="service-card opacity-0 animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`service-card-${index}`}
            >
              <div className="service-icon">
                {service.icon}
              </div>
              <h3 
                className="text-2xl font-semibold text-[#0A192F]"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                {service.title}
              </h3>
              <p className="text-[#475569] leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2 mt-4">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-[#475569]">
                    <CheckCircle size={16} className="text-[#004EEB]" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="section-padding bg-[#F4F6F8]" data-testid="about-section">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative opacity-0 animate-fade-in-up">
            <img
              src="https://images.unsplash.com/photo-1573497019414-e44d0759d00e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMGRldmVsb3BlciUyMHRlYW0lMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc3NTcxNDA4NXww&ixlib=rb-4.1.0&q=85"
              alt="Team collaboration"
              className="w-full h-[500px] object-cover"
              data-testid="about-image"
            />
          </div>

          {/* Content */}
          <div className="space-y-8 opacity-0 animate-fade-in-up animation-delay-200">
            <div>
              <p className="overline mb-4">About Us</p>
              <h2 
                className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A192F] mb-6"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Built on trust,<br />driven by results
              </h2>
            </div>
            
            <p className="text-lg text-[#475569] leading-relaxed">
              Lotus Infotek is dedicated to extending various online software services 
              and support to individuals and private companies who are interested in 
              cost effective, reliable and robust solutions for their software requirements.
            </p>

            <p className="text-lg text-[#475569] leading-relaxed">
              We believe in putting people over profit and service over sales. Our team 
              of dedicated professionals works closely with each client to understand 
              their unique challenges and deliver tailored solutions that drive real results.
            </p>

            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-[#E2E8F0]">
              <div>
                <p className="text-4xl font-bold text-[#004EEB]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>10+</p>
                <p className="text-sm text-[#475569] mt-1">Years Experience</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#004EEB]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>200+</p>
                <p className="text-sm text-[#475569] mt-1">Projects Completed</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#004EEB]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>50+</p>
                <p className="text-sm text-[#475569] mt-1">Happy Clients</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section section-padding" data-testid="contact-section">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#E0F2FE] mb-4">
                Get In Touch
              </p>
              <h2 
                className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Let's build something great together
              </h2>
              <p className="text-lg text-white/70 leading-relaxed">
                Have a project in mind? We'd love to hear about it. 
                Reach out and let's start a conversation.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10">
                  <Mail size={20} className="text-[#E0F2FE]" />
                </div>
                <div>
                  <p className="text-sm text-white/50">Email us at</p>
                  <a 
                    href="mailto:infoteklotus@gmail.com" 
                    className="text-white hover:text-[#E0F2FE] transition-colors"
                    data-testid="contact-email-link"
                  >
                    infoteklotus@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10">
                  <Globe size={20} className="text-[#E0F2FE]" />
                </div>
                <div>
                  <p className="text-sm text-white/50">Visit our website</p>
                  <a 
                    href="https://lotusinfotek.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:text-[#E0F2FE] transition-colors"
                    data-testid="contact-website-link"
                  >
                    lotusinfotek.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 p-8 border border-white/10">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm text-white/70 mb-2">
                  Your Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  className="contact-input rounded-none bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm text-white/70 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                  className="contact-input rounded-none bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm text-white/70 mb-2">
                  Your Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows={5}
                  required
                  className="contact-input rounded-none bg-white/10 border-white/20 text-white placeholder:text-white/50 resize-none"
                  data-testid="contact-input-message"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full btn-primary rounded-none"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#0A192F] border-t border-white/10 py-8" data-testid="footer">
      <div className="section-container">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Lotus Infotek. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a 
              href="https://lotusinfotek.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white text-sm transition-colors"
            >
              lotusinfotek.com
            </a>
            <a 
              href="mailto:infoteklotus@gmail.com"
              className="text-white/50 hover:text-white text-sm transition-colors"
            >
              infoteklotus@gmail.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
